<template>
  <div id="dashboard">
    <Navbar current="Dashboard" />
    <Sidebar />
    <Container col="col-12">
      <Header
        icon="fa-solid fa-chart-line"
        title="Dashboard"
        :tabs="tabs"
        @tabClicked="handleTabClicked"
      />

      <!-- Aba Principal -->
      <div v-if="activeTab === 'principal'">
        <LoadingContainer v-if="loadingGeneral" />
        <div v-else-if="generalData">
          <!-- SEÇÃO 1: Cards Big Numbers - Totais por tipo profissional -->
          <h2 class="text-paragraph mb-3">Totais de usuários por tipo de profissional</h2>
          <div class="row mb-4">
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-user-doctor me-2"></i>Médicos
                  </h5>
                  <div class="display-4">{{ generalData.totals.doctor || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-graduation-cap me-2"></i>Estudantes
                  </h5>
                  <div class="display-4">{{ generalData.totals.student || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-tooth me-2"></i>Dentistas
                  </h5>
                  <div class="display-4">{{ generalData.totals.dentist || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-user me-2"></i>Outros
                  </h5>
                  <div class="display-4">{{ generalData.totals.other || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-handshake me-2"></i>Parceiros
                  </h5>
                  <div class="display-4">{{ generalData.totals.partner || 0 }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 2: Gráfico de barras - Cadastros mensais -->
          <h2 class="text-paragraph mb-3">Cadastros mensais</h2>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px;">
              <BarChart v-if="monthlySignupsChart.labels.length" :chartData="monthlySignupsChart" />
            </div>
          </div>

          <!-- SEÇÃO 3: Gráfico de barras agrupadas - Por tipo profissional -->
          <h2 class="text-paragraph mb-3">Cadastros por tipo profissional</h2>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px;">
              <GroupedBarChart v-if="professionalTypeChart.labels.length" :chartData="professionalTypeChart" />
            </div>
          </div>

          <!-- SEÇÃO 4, 5 e 6: Assinantes em uma linha -->
          <div class="row mb-4">
            <!-- Total assinantes -->
            <div class="col-12 col-lg-2">
              <h2 class="text-paragraph mb-3">Total de assinantes</h2>
              <div class="card h-100">
                <div class="card-body text-center d-flex flex-column justify-content-center">
                  <div class="display-2">{{ generalData.subscriber_count || 0 }}</div>
                  <p class="text-muted mb-0">assinantes ativos</p>
                </div>
              </div>
            </div>

            <!-- Gráfico de pizza - Tipos de assinantes -->
            <div class="col-12 col-lg-4">
              <h2 class="text-paragraph mb-3">Tipos de assinantes</h2>
              <div class="card h-100">
                <div class="card-body" style="height: 250px;">
                  <PieChart v-if="subscribersTypeChart.labels.length" :chartData="subscribersTypeChart" />
                </div>
              </div>
            </div>

            <!-- Gráfico de barras - Assinaturas por gateway -->
            <div class="col-12 col-lg-6">
              <h2 class="text-paragraph mb-3">Assinaturas mensais por gateway</h2>
              <div class="card h-100">
                <div class="card-body" style="height: 250px;">
                  <StackedBarChart v-if="gatewayChart.labels.length" :chartData="gatewayChart" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Aba Uso -->
      <div v-if="activeTab === 'uso'">
        <LoadingContainer v-if="loadingUsage" />
        <div v-else-if="usageData">
          <!-- SEÇÃO 1: Cards Big Numbers - Totais de uso -->
          <h2 class="text-paragraph mb-3">Totais de uso</h2>
          <div class="row mb-4">
            <div class="col-12 col-lg-3">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-prescription me-2"></i>Receitas
                  </h5>
                  <div class="display-4">{{ usageData.totals.recipes || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-3">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-hospital-user me-2"></i>Pacientes
                  </h5>
                  <div class="display-4">{{ usageData.totals.patients || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-3">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-notes-medical me-2"></i>Prontuários
                  </h5>
                  <div class="display-4">{{ usageData.totals.medical_records || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-3">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-star me-2"></i>Favoritos
                  </h5>
                  <div class="display-4">{{ usageData.totals.favorited_contents || 0 }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 2: Gráficos de receitas -->
          <div class="row mb-4" style="min-height: 350px;">
            <!-- Gráfico de pizza - Tipos de receita -->
            <div class="col-12 col-lg-4 mb-3 mb-lg-0">
              <h2 class="text-paragraph mb-3">Tipos de receita emitidas</h2>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <PieChart v-if="recipeTypeChart.labels.length" :chartData="recipeTypeChart" />
                </div>
              </div>
            </div>

            <!-- Gráfico de barras - Receitas mensais -->
            <div class="col-12 col-lg-8 mb-3 mb-lg-0">
              <h2 class="text-paragraph mb-3">Emissão de receitas mensal</h2>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <BarChart v-if="monthlyRecipesChart.labels.length" :chartData="monthlyRecipesChart" />
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 3: Uso dos médicos assinantes -->
          <div class="row mb-4">
            <div class="col-12 col-lg-3">
              <h2 class="text-paragraph mb-3">Médicos pagantes</h2>
              <div class="card h-100">
                <div class="card-body text-center d-flex flex-column justify-content-center">
                  <div class="display-2">{{ usageData.doctor_subscriber_usage_type.total_medicos_pagantes || 0 }}</div>
                  <p class="text-muted mb-0">médicos assinantes</p>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-9">
              <h2 class="text-paragraph mb-3">Tipo de uso dos médicos assinantes</h2>
              <div class="card h-100">
                <div class="card-body" style="height: 250px;">
                  <PieChart v-if="doctorUsageChart.labels.length" :chartData="doctorUsageChart" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Aba Churn -->
      <div v-if="activeTab === 'churn'">
        <LoadingContainer v-if="loadingChurn" />
        <div v-else-if="churnData">
          <!-- SEÇÃO 1: Cancelamentos mensais -->
          <h2 class="text-paragraph mb-3">Cancelamentos por mês (Stripe)</h2>
          <div class="card mb-4">
            <div class="card-body" style="height: 280px; position: relative;">
              <BarChart v-if="monthlyCancellationsChart.labels.length" :chartData="monthlyCancellationsChart" />
            </div>
          </div>

          <!-- SEÇÃO 2: Churn rate mensal -->
          <h2 class="text-paragraph mb-3">Churn rate mensal Stripe (%)</h2>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <DualAxisLineChart v-if="churnRateChart.labels.length" :chartData="churnRateChart" />
            </div>
          </div>

          <!-- SEÇÃO 3: Cohort de churn -->
          <h2 class="text-paragraph mb-3">Cohort de churn Stripe (tempo até cancelar)</h2>
          <div class="card mb-4">
            <div class="card-body" style="height: 280px; position: relative;">
              <BarChart v-if="churnCohortChart.labels.length" :chartData="churnCohortChart" />
            </div>
          </div>
        </div>
      </div>
    </Container>
  </div>
</template>

<script>
import DashboardController from "./DashboardController";
export default DashboardController;
</script>
